import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useRoadSafetyPromotion,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Safety',
  content: [
    {
      title: 'Road safety promotion',
      description:
  <Text fontSize="inherit" mb={2}>
    The expenditure on promotion, education and advertising associated with Road Safety.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools</Link>
  </Text>,
    },
  ],
};

function TransportProgrammeDeliveryRoadSafety() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: roadSafetyPromotion, isLoading: isLoadingRoadSafety } = useRoadSafetyPromotion();
  const isLoading = isLoadingRoadSafety;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  const roadSafetyPromotionYears = [...new Set(roadSafetyPromotion?.map((item) => item.Year))];
  const roadSafetyPromotionData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: roadSafetyPromotion?.map((item) => ({ x: item.Year, y: item.FundValue / 1000000 })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: roadSafetyPromotion?.map((item) => ({ x: item.Year, y: item.PeerGroupValue / 1000000 })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: roadSafetyPromotion?.map((item) => ({ x: item.Year, y: [item.LowPercentile / 1000000, item.HighPercentile / 1000000] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const roadSafetyChartSeries = filterChartDataByName(
    roadSafetyPromotionData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Road Safety"
      informationSource={informationSource}
      isLoading={isLoading || !roadSafetyPromotion}
      lockYear={roadSafetyPromotion?.[0]?.LockYear}
    >
      <LineRangeAreaChart
        title="Road safety promotion"
        categories={roadSafetyPromotionYears}
        series={roadSafetyChartSeries}
        type="rangeArea"
        width="280"
        height="280"
        stroke={stroke}
        markers={markers}
        prefix="$"
        suffix="M"
        decimalPoints={1}
        yAxisDecimalPoints={1}
      />
    </ReportSection>
  );
}

export default TransportProgrammeDeliveryRoadSafety;
